import * as Phaser from "phaser";
import dialogueTree from "./dialogue.json";
import { DialogueUI } from "./dialogue_ui.js";
import { updateProgress } from "./api.js";
import { PALETTE as C } from "./palette.js";
import { drawRoom, TILE, ROOM_W, ROOM_H, ROOM_COLS, ROOM_ROWS } from "./room.js";
import { drawFurniture } from "./furniture.js";
import { drawLighting } from "./lighting.js";
import { drawGridOverlay } from "./grid_overlay.js";
import { TavernHUD } from "./hud.js";

export class TavernScene extends Phaser.Scene {
  constructor() {
    super("Tavern");
  }

  init(data) {
    this.progress = data.progress || {};
    this.dialogueRoot = data.dialogueRoot;
    this.hudRoot = data.hudRoot;
    this.assets = data.assets || {};
  }

  preload() {
    // Load PixelLab sprites from the asset path map passed in via init.
    for (const [key, url] of Object.entries(this.assets)) {
      if (!url) continue;
      if (key === "tileset_json") this.load.json(key, url);
      else this.load.image(key, url);
    }
  }

  create() {
    this.ui = new DialogueUI(this.dialogueRoot);

    // Draw the world, bottom layer first.
    const wallCollisions = drawRoom(this);
    const { collisions: furnitureCollisions, interactions } = drawFurniture(this);

    // Grid overlay: off in free-roam by default, toggleable via the HUD.
    this.gridOverlay = drawGridOverlay(this, ROOM_COLS, ROOM_ROWS, TILE);

    // HUD -- grid toggle, zoom, mode indicator.
    if (this.hudRoot) {
      this.hud = new TavernHUD(this.hudRoot, { mode: "At the Tavern" });
      this.hud.on("grid-toggle", ({ on }) => this.gridOverlay.setVisible(on));
      this.hud.on("zoom", ({ delta }) => {
        const z = this.cameras.main.zoom;
        this.cameras.main.setZoom(Phaser.Math.Clamp(z + delta, 0.8, 3));
      });
    }

    // ═══ The Scholar (a patron sitting at the middle table, reading) ═══
    const scholarX = TILE * 10;
    const scholarY = TILE * 8.2;
    if (this.textures.exists("scholar")) {
      this.scholar = this.add.image(scholarX, scholarY, "scholar");
      this.fitSpriteToTiles(this.scholar, 1.1);
      this.scholar.setDepth(4);
      // Gentle breathing / reading sway
      this.tweens.add({ targets: this.scholar, y: scholarY - 1, yoyo: true, repeat: -1, duration: 3200, ease: "Sine.inOut" });
    }
    interactions.scholar = {
      x: scholarX,
      y: scholarY + TILE * 1.3,
      radius: TILE * 1.8,
      label: "Speak to the scholar",
      slug: "scholar",
      priority: 5
    };

    // Lighting sits above the floor/furniture but below NPCs and the player.
    drawLighting(this);

    this.collisions = [...wallCollisions, ...furnitureCollisions];

    // ═══ The Archivist (behind the bar, at the top of the room) ═══
    const barCentreX = TILE * 10;
    const behindBarY = TILE * 3.2;
    // Rim glow beneath
    this.add.circle(barCentreX, behindBarY + 4, TILE * 0.8, C.gold, 0.08);
    if (this.textures.exists("archivist_south")) {
      this.archivist = this.add.image(barCentreX, behindBarY, "archivist_south");
      // Scale to ~1.5 tiles tall so the character reads on the 32px grid.
      this.fitSpriteToTiles(this.archivist, 1.4);
    } else {
      this.archivist = this.add.rectangle(barCentreX, behindBarY, TILE * 0.8, TILE * 1.3, C.gold);
    }
    this.archivist.setDepth(3);
    // Label floats above
    this.archivistLabel = this.add.text(barCentreX, behindBarY - TILE * 2.2, "The Archivist", {
      fontFamily: "Cinzel, serif",
      fontSize: "11px",
      color: "#c9a96e",
    }).setOrigin(0.5).setDepth(3);
    // Subtle bob
    this.tweens.add({ targets: this.archivist, y: behindBarY - 3, yoyo: true, repeat: -1, duration: 2500, ease: "Sine.inOut" });

    // Archivist interaction zone is in front of the bar.
    interactions.archivist = {
      x: barCentreX,
      y: TILE * 6,
      radius: TILE * 2.5,
      label: "Speak to the Archivist",
      slug: "archivist",
      priority: 10
    };
    this.interactions = interactions;

    // Draw subtle indicators at interaction points.
    this.interactMarkers = {};
    for (const [key, point] of Object.entries(this.interactions)) {
      const marker = this.add.circle(point.x, point.y, 5, C.gold, 0);
      marker.setStrokeStyle(1, C.gold, 0);
      this.interactMarkers[key] = marker;
    }

    // ═══ Player / Traveller ═══
    // Uses the PixelLab traveller sprite when available, otherwise a simple hooded block.
    const spawnX = TILE * 10;
    const spawnY = ROOM_H - TILE * 2.5;
    this.player = this.add.container(spawnX, spawnY);
    this.player.setDepth(5);
    const pShadow = this.add.ellipse(0, TILE * 0.5, TILE * 0.7, TILE * 0.18, C.bg, 0.45);
    this.player.add(pShadow);
    if (this.textures.exists("traveller_south")) {
      this.playerSprite = this.add.image(0, 0, "traveller_south");
      this.fitSpriteToTiles(this.playerSprite, 1.3);
      this.player.add(this.playerSprite);
    } else {
      const pBody = this.add.rectangle(0, 0, TILE * 0.6, TILE * 0.9, C.cream);
      pBody.setStrokeStyle(1, C.gold);
      const pHood = this.add.circle(0, -TILE * 0.45, TILE * 0.28, C.gold);
      this.player.add([pBody, pHood]);
    }

    // ═══ Input ═══
    this.cursors = this.input.keyboard.createCursorKeys();
    this.wasd = {
      up: this.input.keyboard.addKey("W"),
      down: this.input.keyboard.addKey("S"),
      left: this.input.keyboard.addKey("A"),
      right: this.input.keyboard.addKey("D"),
    };
    this.interactKey = this.input.keyboard.addKey("E");
    this.spacebar = this.input.keyboard.addKey("SPACE");

    // ═══ Hint / interact label ═══
    this.hint = this.add.text(ROOM_W / 2, ROOM_H - TILE * 0.5, "Arrows or WASD to move. Space or E to interact.", {
      fontFamily: "Inter, sans-serif",
      fontSize: "11px",
      color: "#7a7568",
    }).setOrigin(0.5).setDepth(50);

    this.interactPrompt = this.add.text(0, 0, "", {
      fontFamily: "Inter, sans-serif",
      fontSize: "12px",
      color: "#c9a96e",
      backgroundColor: "rgba(22,22,37,0.85)",
      padding: { x: 6, y: 3 },
    }).setOrigin(0.5, 1).setDepth(50).setVisible(false);

    this.busy = false;
    this.lastStep = 0;
    this.facing = "down";

    // ═══ Camera: follow the player, zoom in so the room feels intimate ═══
    this.cameras.main.setBounds(0, 0, ROOM_W, ROOM_H);
    this.cameras.main.setBackgroundColor(0x0d0d14);
    this.cameras.main.startFollow(this.player, true, 0.12, 0.12);
    this.cameras.main.setZoom(this.calculateZoom());
    this.hint.setScrollFactor(0);
    this.interactPrompt.setScrollFactor(0);
    // Reposition hint in screen space.
    this.hint.setPosition(this.scale.width / 2, this.scale.height - 20);
    this.hint.setDepth(100);
    this.interactPrompt.setDepth(100);

    this.scale.on("resize", this.handleResize, this);

    // ═══ Touch / pointer: tap-to-move, tap-near-interactable-to-talk ═══
    this.moveTarget = null;
    this.input.on("pointerdown", (pointer) => {
      if (this.busy) return;
      const worldPoint = { x: pointer.worldX, y: pointer.worldY };

      // If tap is near an interactable, head toward it and trigger on arrival.
      const tapped = this.interactionNearPoint(worldPoint);
      if (tapped) {
        this.moveTarget = { x: tapped.point.x, y: tapped.point.y, tappedKey: tapped.key };
      } else {
        this.moveTarget = { x: worldPoint.x, y: worldPoint.y, tappedKey: null };
      }
    });
  }

  // Scale a sprite so its larger dimension is N tiles. Keeps aspect ratio.
  fitSpriteToTiles(sprite, targetTiles) {
    const maxDim = Math.max(sprite.width, sprite.height);
    if (!maxDim) return;
    sprite.setScale((targetTiles * TILE) / maxDim);
  }

  // Pick a zoom such that the player sees a comfortable portion of the room.
  // On narrow/tall mobile screens we zoom in; on wide desktop we show more.
  calculateZoom() {
    const w = this.scale.width;
    const h = this.scale.height;
    // Target: at least ~280px of world height visible, but never under 1.5x.
    const targetWorldH = Math.max(320, Math.min(ROOM_H, h / 1.5));
    const zoomH = h / targetWorldH;
    const targetWorldW = Math.max(420, Math.min(ROOM_W, w / zoomH));
    const zoomW = w / targetWorldW;
    return Math.min(zoomH, zoomW);
  }

  handleResize(gameSize) {
    this.cameras.main.setZoom(this.calculateZoom());
    if (this.hint) this.hint.setPosition(gameSize.width / 2, gameSize.height - 20);
  }

  // Find an interactable near a tapped world point. Interaction zones sit at
  // the "stand here to talk" spot in front of the NPC, not on the sprite itself,
  // so tap radius is deliberately large to catch taps on the sprite above it.
  interactionNearPoint(p) {
    let nearest = null;
    let minDist = Infinity;
    for (const [key, point] of Object.entries(this.interactions)) {
      const d = Phaser.Math.Distance.Between(p.x, p.y, point.x, point.y);
      const tapR = point.tapRadius ?? Math.max(point.radius * 2.5, TILE * 3);
      if (d < tapR && d < minDist) { minDist = d; nearest = { key, point }; }
    }
    return nearest;
  }

  update(time, dt) {
    if (this.busy) return;

    const speed = 140; // px/sec
    const delta = dt / 1000;

    const left = this.cursors.left.isDown || this.wasd.left.isDown;
    const right = this.cursors.right.isDown || this.wasd.right.isDown;
    const up = this.cursors.up.isDown || this.wasd.up.isDown;
    const down = this.cursors.down.isDown || this.wasd.down.isDown;

    let dx = 0, dy = 0;
    if (left)  dx -= 1;
    if (right) dx += 1;
    if (up)    dy -= 1;
    if (down)  dy += 1;

    // If no keyboard input, honour the tap target.
    if (dx === 0 && dy === 0 && this.moveTarget) {
      const tx = this.moveTarget.x - this.player.x;
      const ty = this.moveTarget.y - this.player.y;
      const dist = Math.hypot(tx, ty);
      if (dist < 6) {
        // Arrived. If we tapped an interactable, open it now.
        const tapped = this.moveTarget.tappedKey;
        this.moveTarget = null;
        if (tapped && this.interactions[tapped]) {
          this.openInteraction(tapped, this.interactions[tapped]);
          return;
        }
      } else {
        dx = tx / dist;
        dy = ty / dist;
      }
    } else if (dx !== 0 || dy !== 0) {
      // Keyboard input overrides tap target.
      this.moveTarget = null;
    }

    // Normalise diagonals so you don't move faster.
    if (dx !== 0 && dy !== 0 && Math.abs(dx) === 1 && Math.abs(dy) === 1) {
      dx *= 0.7071;
      dy *= 0.7071;
    }

    if (dx !== 0 || dy !== 0) {
      const nx = this.player.x + dx * speed * delta;
      const ny = this.player.y + dy * speed * delta;
      // Try axes independently so the player slides along walls.
      if (!this.collidesAt(nx, this.player.y)) this.player.x = nx;
      if (!this.collidesAt(this.player.x, ny)) this.player.y = ny;

      // Facing.
      if (Math.abs(dx) > Math.abs(dy)) this.facing = dx > 0 ? "right" : "left";
      else this.facing = dy > 0 ? "down" : "up";

      // Subtle step bob.
      if (time - this.lastStep > 180) {
        this.player.list[1].y = (this.player.list[1].y === 0) ? -1 : 0;
        this.lastStep = time;
      }
    }

    // Player bounds clamp (defence against bugs).
    this.player.x = Phaser.Math.Clamp(this.player.x, TILE * 1.5, ROOM_W - TILE * 1.5);
    this.player.y = Phaser.Math.Clamp(this.player.y, TILE * 1.5, ROOM_H - TILE * 1.5);

    // Determine nearest interactable.
    const nearest = this.nearestInteraction();
    if (nearest) {
      this.interactPrompt.setText(nearest.point.label);
      this.positionInteractPromptAbovePlayer();
      this.interactPrompt.setVisible(true);
      this.hint.setAlpha(0.3);

      if (Phaser.Input.Keyboard.JustDown(this.interactKey) || Phaser.Input.Keyboard.JustDown(this.spacebar)) {
        this.openInteraction(nearest.key, nearest.point);
      }
    } else {
      this.interactPrompt.setVisible(false);
      this.hint.setAlpha(1);
    }
  }

  // interactPrompt rides screen-space from the player's world position
  positionInteractPromptAbovePlayer() {
    const cam = this.cameras.main;
    const sx = (this.player.x - cam.worldView.x) * cam.zoom;
    const sy = (this.player.y - TILE * 1.2 - cam.worldView.y) * cam.zoom;
    this.interactPrompt.setPosition(sx, sy);
  }

  collidesAt(x, y) {
    const halfW = TILE * 0.35;
    const halfH = TILE * 0.45;
    const left = x - halfW;
    const right = x + halfW;
    const top = y - halfH;
    const bottom = y + halfH;
    for (const r of this.collisions) {
      if (right > r.x && left < r.x + r.w && bottom > r.y && top < r.y + r.h) return true;
    }
    return false;
  }

  nearestInteraction() {
    let nearest = null;
    let minDist = Infinity;
    for (const [key, point] of Object.entries(this.interactions)) {
      const d = Phaser.Math.Distance.Between(this.player.x, this.player.y, point.x, point.y);
      if (d < point.radius && d < minDist) {
        minDist = d;
        nearest = { key, point, distance: d };
      }
    }
    return nearest;
  }

  async openInteraction(key, point) {
    this.busy = true;
    try {
      if (key === "archivist") await this.flowArchivist();
      else if (key === "scholar") await this.flowScholar();
      else await this.flowStatic(key);
    } finally {
      this.busy = false;
    }
  }

  async flowScholar() {
    await this.runNode("scholar", "default");
    await updateProgress({ event: "conversation", slug: "scholar:greet" });
  }

  async flowArchivist() {
    const isReturning = (this.progress.name && this.progress.name.length > 0);
    const startKey = isReturning ? "greet_returning" : "greet_unknown";
    await this.runNode("archivist", startKey);
    await updateProgress({ event: "conversation", slug: "archivist:greet" });
  }

  async flowStatic(key) {
    const hasName = this.progress.name && this.progress.name.length > 0;
    const subKey = (key === "ledger" && hasName) ? "named" : "default";
    await this.runNode(key, subKey);
    await updateProgress({ event: "conversation", slug: `${key}:${subKey}` });
  }

  async runNode(npc, nodeKey) {
    const node = dialogueTree[npc] && dialogueTree[npc][nodeKey];
    if (!node) return;

    const ctx = { name: this.progress.name || "traveller" };
    const result = await this.ui.present(node, ctx);

    // Name input path (only used by the Archivist's greet_unknown).
    if (result.type === "name") {
      this.progress.name = result.value;
      const updated = await updateProgress({
        name: result.value,
        event: "conversation",
        slug: `${npc}:${nodeKey}:named`
      });
      if (updated) Object.assign(this.progress, updated);

      // Continue to the explicit next node if one was declared.
      if (node.next) {
        const [nextNpc, nextKey] = node.next.split(":");
        await this.runNode(nextNpc, nextKey);
      }
      return;
    }

    // Choice path.
    if (result.type === "choice") {
      if (result.effect === "signup" && !this.progress.signed_in) {
        await updateProgress({ event: "quest", slug: `${npc}:named` });
        window.location.href = "/users/sign_up";
        return;
      }
      if (result.effect === "next" && result.next) {
        const [nextNpc, nextKey] = result.next.split(":");
        await this.runNode(nextNpc, nextKey);
        return;
      }
      // "dismiss" or unknown -> fall through.
      return;
    }
  }
}
